"use client";

import { createContext, type ReactNode, useContext, useMemo } from "react";
import { useMerchants } from "@/components/features/merchants/hooks/queries";
import type { ActiveMerchantValue, AvailableMerchant } from "@/types";

const ActiveCompanyContext = createContext<ActiveMerchantValue | null>(null);

export function ActiveMerchantProvider({
  value,
  children,
}: {
  value: ActiveMerchantValue;
  children: ReactNode;
}) {
  return (
    <ActiveCompanyContext.Provider value={value}>
      {children}
    </ActiveCompanyContext.Provider>
  );
}

// activeId + canSeeAll — synchronous read, no fetch
export function useActiveMerchant(): ActiveMerchantValue {
  const ctx = useContext(ActiveCompanyContext);
  if (!ctx) {
    throw new Error(
      "useActiveMerchant must be used inside ActiveMerchantProvider",
    );
  }
  return ctx;
}

// Available merchants list — backed by the feature-slice useMerchants query
export function useAvailableMerchants(): ReadonlyArray<AvailableMerchant> {
  const { items } = useMerchants();
  return useMemo<AvailableMerchant[]>(
    () =>
      items.map((m) => ({
        id: m.id,
        name: m.name,
        brand_color: m.brand_color ?? null,
      })),
    [items],
  );
}
